import { applyTransaction, arrayAdd, arrayRemove, arrayUpdate, combineQueries, filterNilValue } from '@datorama/akita';
import { type AxiosError } from 'axios';
import { cloneDeep, isEqual, isUndefined, omit, omitBy } from 'lodash-es';
import { distinctUntilChanged, map, type Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import kpiScopesStore, { type KPIScopesState } from './store';
import { activeOrganisationId$ } from 'context/OrganisationContext/query';
import { context$ as periodContext$ } from 'context/PeriodContext/query';
import { includeSubsidiaries$ } from 'context/UserContext/query';
import CRUDService, { type QueryDependency, type QueryResolvedValues } from 'state/CRUDService';
import { query as kpiScopesQuery } from 'state/KPIScope/query';
import { query } from 'state/KPISet/query';
import store from 'state/KPISet/store';
import { activeEntityId$ as activeReportingStandardId$ } from 'state/ReportingStandards/query';
import {
   type Attachment,
   type ErrorObject,
   type KPI,
   type KpiContent,
   type KpiField,
   type KpiScope,
   type KpiSub,
   type Organisation,
   type Period,
   type ReportingStandard,
} from 'types/models';
import { trackAiAction } from 'utils/aiTelemetry';
import { deleteAttachmentHandler } from 'utils/attachmentHandler';
import { uploadFilesCommon } from 'utils/uploadFiles';

const childKeys = ['kpis', 'kpiSubs', 'kpiContents', 'kpiFields'];

const hasEveryScopeMateriality = (scopes: KpiScope[], from: string, to: string, organisationId?: Organisation['id'], isMaterial?: boolean) =>
   (isMaterial
      ? true
      : scopes.filter((kpiScope) => kpiScope.from === from && kpiScope.to === to && kpiScope.organisationId === organisationId).length > 0) &&
   scopes
      .filter((kpiScope) => kpiScope.from === from && kpiScope.to === to && kpiScope.organisationId === organisationId)
      .every((kpiScope) => !!kpiScope.isMaterial === isMaterial);

const hasEveryScopeMaterialityNested = (node: any, from: string, to: string, organisationId?: Organisation['id'], isMaterial?: boolean) => {
   if (childKeys.some((childKey) => childKey in node)) {
      const childKey = childKeys.find((childKey_) => childKey_ in node);

      return (
         hasEveryScopeMateriality(node.kpiScopes, from, to, organisationId, isMaterial) &&
         childKey &&
         node[childKey].every((childNode: any) => hasEveryScopeMaterialityNested(childNode, from, to, organisationId, isMaterial))
      );
   }

   return hasEveryScopeMateriality(node.kpiScopes, from, to, organisationId, isMaterial);
};

const calculateScope = (nodes: any[], from: string, to: string, organisation: Organisation) => {
   if (nodes.every((node) => hasEveryScopeMaterialityNested(node, from, to, organisation.id, true))) {
      return true;
   }
   if (nodes.every((node) => hasEveryScopeMaterialityNested(node, from, to, organisation.id, false)) && !organisation?.allowMaterialityAssessment) {
      return false;
   }
   return null;
};

export default class KPIScopesService extends CRUDService<KPIScopesState> {
   private static instance: KPIScopesService;
   private suggestionsController: AbortController | null = null;
   private scopesController: AbortController | null = null;
   private scopesObservable;
   private scopesSubscription?: Subscription;
   kpiScopesQuery;

   constructor() {
      if (!KPIScopesService.instance) {
         super('kpiscopes', store as any, query as any, {
            includeSubsidiaries: true,
            useOrganisationContext: true,
            usePeriodContext: true,
            useReportingStandardContext: true,
         });
         this.kpiScopesStore = kpiScopesStore;
         this.kpiScopesQuery = kpiScopesQuery;
         this.suggestionsController = new AbortController();
         this.scopesController = new AbortController();
         this.scopesObservable = this.getScopeQueryParams();

         KPIScopesService.instance = this;
      }
      return KPIScopesService.instance;
   }

   setError(error: ErrorObject, store_: any = this.store) {
      if (error?.response?.status === 502 || error?.response?.code === 502) {
         return window.location.reload();
      }

      return store_.setError({
         variant: 'error',
         message: `${error?.response?.data?.message ?? error.message ?? ''} ${error?.response?.data?.error ?? ''}`,
         key: `${store?.storeName}#${uuidv4()}`,
         store: store?.storeName,
      });
   }

   #changeKPIScopes(entity: KpiScope, from: string, to: string, organisationId: Organisation['id'], changes: any, updateChildScopes: boolean) {
      const modifiedEntity = cloneDeep(entity);

      if (updateChildScopes) {
         childKeys.forEach((childKey) => {
            if (modifiedEntity?.[childKey]) {
               modifiedEntity[childKey] = modifiedEntity[childKey].map((child: any) =>
                  this.#changeKPIScopes(child, from, to, organisationId, changes, updateChildScopes)
               );
            }
         });
      }

      if (modifiedEntity?.kpiScopes?.length > 0) {
         const relevantScope = modifiedEntity.kpiScopes.find(
            (kpiScope: KpiScope) => kpiScope.from === from && kpiScope.to === to && kpiScope.organisationId === organisationId
         );
         const result = {
            ...modifiedEntity,
            kpiScopes: relevantScope
               ? modifiedEntity.kpiScopes.map((kpiScope: KpiScope) =>
                    kpiScope.from === from && kpiScope.to === to && kpiScope.organisationId === organisationId
                       ? {
                            ...omit(kpiScope, ['stakeholderImpact', 'businessImpact', 'reason']),
                            ...changes,
                            updated: true,
                         }
                       : kpiScope
                 )
               : [
                    ...modifiedEntity.kpiScopes,
                    {
                       id: Math.min(0, ...modifiedEntity.kpiScopes.map(({ id }: KpiScope) => id)) - 1,
                       from,
                       to,
                       organisationId,
                       businessImpact: undefined,
                       stakeholderImpact: undefined,
                       reason: '',
                       ...changes,
                       updated: true,
                    },
                 ],
         };
         return result;
      }

      return {
         ...modifiedEntity,
         kpiScopes: [
            ...modifiedEntity.kpiScopes,
            {
               id: Math.min(0, ...modifiedEntity.kpiScopes.map(({ id }: KpiScope) => id)) - 1,
               from,
               to,
               organisationId,
               businessImpact: undefined,
               stakeholderImpact: undefined,
               reason: '',
               ...changes,
               updated: true,
            },
         ],
      };
   }

   #changeKPIScope(
      entityType: string | undefined,
      entityId: KpiScope['id'] | undefined,
      from: string,
      to: string,
      organisation: Organisation,
      changes: any,
      updateChildScopes: boolean
   ) {
      const kpiSet = this.query.getAll();

      let done = false;

      return applyTransaction(() => {
         if (!entityType && !entityId) {
            return kpiSet.forEach((kpiArea) =>
               this.store.update(kpiArea.id, this.#changeKPIScopes(kpiArea, from, to, organisation.id, changes, updateChildScopes))
            );
         }

         if (!done) {
            for (const kpiArea of kpiSet) {
               if (!done && entityType === 'kpiArea' && kpiArea.id === entityId) {
                  done = true;
                  this.store.update(entityId, this.#changeKPIScopes(kpiArea, from, to, organisation.id, changes, updateChildScopes));
               }
               if (!done && entityType !== 'kpiArea') {
                  for (const kpi of kpiArea.kpis) {
                     if (!done && entityType === 'kpi' && kpi.id === entityId) {
                        done = true;
                        this.store.update(kpiArea.id, ({ kpis }) => ({
                           kpis: arrayUpdate(kpis, kpi.id, this.#changeKPIScopes(kpi, from, to, organisation.id, changes, updateChildScopes)),
                        }));
                     }

                     if (!done && entityType !== 'kpi') {
                        for (const kpiSub of kpi.kpiSubs) {
                           if (!done && entityType === 'kpiSub' && kpiSub.id === entityId) {
                              done = true;
                              this.store.update(kpiArea.id, (entity) => ({
                                 ...entity,
                                 kpis: entity.kpis.map((kpi_: KPI) =>
                                    kpi_.id !== kpi.id
                                       ? kpi_
                                       : {
                                            ...kpi_,
                                            kpiSubs: arrayUpdate(
                                               kpi_.kpiSubs,
                                               kpiSub.id,
                                               this.#changeKPIScopes(kpiSub, from, to, organisation.id, changes, updateChildScopes)
                                            ),
                                         }
                                 ),
                              }));
                           }

                           if (!done && entityType !== 'kpiSub') {
                              for (const kpiContent of kpiSub.kpiContents) {
                                 if (!done && entityType === 'kpiContent' && kpiContent.id === entityId) {
                                    done = true;
                                    this.store.update(kpiArea.id, (entity) => ({
                                       ...entity,
                                       kpis: entity.kpis.map((kpi_: KPI) =>
                                          kpi_.id !== kpi.id
                                             ? kpi_
                                             : {
                                                  ...kpi_,
                                                  kpiSubs: kpi_.kpiSubs.map((kpiSub_: KpiSub) =>
                                                     kpiSub_.id !== kpiSub.id
                                                        ? kpiSub_
                                                        : {
                                                             ...kpiSub_,
                                                             kpiContents: arrayUpdate(
                                                                kpiSub_.kpiContents,
                                                                kpiContent.id,
                                                                this.#changeKPIScopes(
                                                                   kpiContent,
                                                                   from,
                                                                   to,
                                                                   organisation.id,
                                                                   changes,
                                                                   updateChildScopes
                                                                )
                                                             ),
                                                          }
                                                  ),
                                               }
                                       ),
                                    }));
                                 }

                                 if (!done && entityType !== 'kpiContent') {
                                    for (const kpiField of kpiContent.kpiFields) {
                                       if (!done && entityType === 'kpiField' && kpiField.id === entityId) {
                                          done = true;

                                          this.store.update(kpiArea.id, (entity) => ({
                                             ...entity,
                                             kpis: entity.kpis.map((kpi_: KPI) =>
                                                kpi_.id !== kpi.id
                                                   ? kpi_
                                                   : {
                                                        ...kpi_,
                                                        kpiSubs: kpi_.kpiSubs.map((kpiSub_: KpiSub) =>
                                                           kpiSub_.id !== kpiSub.id
                                                              ? kpiSub_
                                                              : {
                                                                   ...kpiSub_,
                                                                   kpiContents: kpiSub_.kpiContents.map((kpiContent_: KpiContent) =>
                                                                      kpiContent_.id !== kpiContent.id
                                                                         ? kpiContent_
                                                                         : {
                                                                              ...kpiContent_,
                                                                              kpiFields: arrayUpdate(
                                                                                 kpiContent_.kpiFields,
                                                                                 kpiField.id,
                                                                                 this.#changeKPIScopes(
                                                                                    kpiField,
                                                                                    from,
                                                                                    to,
                                                                                    organisation.id,
                                                                                    changes,
                                                                                    updateChildScopes
                                                                                 )
                                                                              ),
                                                                           }
                                                                   ),
                                                                }
                                                        ),
                                                     }
                                             ),
                                          }));
                                       }
                                    }
                                 }

                                 if (
                                    'isMaterial' in changes &&
                                    entityType === 'kpiField' &&
                                    kpiContent.kpiScopes.some(
                                       (scope: KpiScope) =>
                                          scope.from === from &&
                                          scope.to === to &&
                                          scope.organisationId === organisation.id &&
                                          scope.isMaterial !== changes.isMaterial
                                    ) &&
                                    kpiContent.kpiFields.length > 0 &&
                                    calculateScope(
                                       kpiContent.kpiFields.filter((entity: KpiField) => entity.id !== entityId),
                                       from,
                                       to,
                                       organisation
                                    ) === changes.isMaterial
                                 ) {
                                    this.store.update(kpiArea.id, (entity) => ({
                                       ...entity,
                                       kpis: entity.kpis.map((kpi_: KPI) =>
                                          kpi_.id !== kpi.id
                                             ? kpi_
                                             : {
                                                  ...kpi_,
                                                  kpiSubs: kpi_.kpiSubs.map((kpiSub_: KpiSub) =>
                                                     kpiSub_.id !== kpiSub.id
                                                        ? kpiSub_
                                                        : {
                                                             ...kpiSub_,
                                                             kpiContents: arrayUpdate(
                                                                kpiSub_.kpiContents,
                                                                kpiContent.id,
                                                                this.#changeKPIScopes(kpiContent, from, to, organisation.id, changes, false)
                                                             ),
                                                          }
                                                  ),
                                               }
                                       ),
                                    }));
                                 }
                              }
                           }

                           if (
                              'isMaterial' in changes &&
                              entityType === 'kpiContent' &&
                              kpiSub.kpiScopes.some(
                                 (scope: KpiScope) =>
                                    scope.from === from &&
                                    scope.to === to &&
                                    scope.organisationId === organisation.id &&
                                    scope.isMaterial === !changes?.isMaterial
                              ) &&
                              kpiSub.kpiContents.length > 0 &&
                              calculateScope(
                                 kpiSub.kpiContents.filter((entity: KpiContent) => entity.id !== entityId),
                                 from,
                                 to,
                                 organisation
                              ) === changes.isMaterial
                           ) {
                              this.store.update(kpiArea.id, (entity) => ({
                                 ...entity,
                                 kpis: entity.kpis.map((kpi_: KPI) =>
                                    kpi_.id !== kpi.id
                                       ? kpi_
                                       : {
                                            ...kpi_,
                                            kpiSubs: arrayUpdate(
                                               kpi_.kpiSubs,
                                               kpiSub.id,
                                               this.#changeKPIScopes(kpiSub, from, to, organisation.id, changes, false)
                                            ),
                                         }
                                 ),
                              }));
                           }
                        }
                     }

                     if (
                        'isMaterial' in changes &&
                        entityType === 'kpiSub' &&
                        kpi.kpiScopes.some(
                           (scope: KpiScope) =>
                              scope.from === from &&
                              scope.to === to &&
                              scope.organisationId === organisation.id &&
                              scope.isMaterial === !changes?.isMaterial
                        ) &&
                        kpi.kpiSubs.length > 0 &&
                        calculateScope(
                           kpi.kpiSubs.filter((entity: KpiSub) => entity.id !== entityId),
                           from,
                           to,
                           organisation
                        ) === changes.isMaterial
                     ) {
                        this.store.update(kpiArea.id, ({ kpis }) => ({
                           kpis: arrayUpdate(kpis, kpi.id, this.#changeKPIScopes(kpi, from, to, organisation.id, changes, false)),
                        }));
                     }
                  }
               }

               if (
                  'isMaterial' in changes &&
                  entityType === 'kpi' &&
                  kpiArea.kpiScopes.some(
                     (scope: KpiScope) =>
                        scope.from === from &&
                        scope.to === to &&
                        scope.organisationId === organisation.id &&
                        scope.isMaterial === !changes?.isMaterial
                  ) &&
                  kpiArea.kpis.length > 0 &&
                  calculateScope(
                     kpiArea.kpis.filter((entity: KPI) => entity.id !== entityId),
                     from,
                     to,
                     organisation
                  ) === changes.isMaterial
               ) {
                  this.store.update(entityId, this.#changeKPIScopes(kpiArea, from, to, organisation.id, changes, false));
               }
            }
         }

         if (!done) {
            throw new Error(`Could not find an entity in KPISet with ID ${entityId} of type ${entityType}`);
         }
         return done;
      });
   }

   setKPIScope(
      entityType: string | undefined,
      entityId: KpiScope['id'] | undefined,
      from: string,
      to: string,
      organisation: Organisation,
      changes: any,
      updateChildScopes = false
   ) {
      return this.#changeKPIScope(entityType, entityId, from, to, organisation, omitBy(changes, isUndefined), updateChildScopes);
   }

   async persistKPIScopes(kpiScopes: KpiScope[], organisations: Organisation[]) {
      const returnValues: any[] = [];
      const allKeys = ['kpiArea', ...childKeys];

      if (kpiScopes.some(({ id }) => !id || id < 0)) {
         await this.httpClient
            .post(
               `/${this.version}/${this.entityName}`,
               kpiScopes.filter(({ id }) => !id || id < 0).map((kpiScope) => omit(kpiScope, ['id']))
            )
            .then((resp) =>
               applyTransaction(() => {
                  returnValues.push(...resp.data);
                  resp.data.forEach((kpiScope: KpiScope) => {
                     const organisation = organisations.find(({ id }) => id === kpiScope.organisationId);

                     if (allKeys.some((childKey) => childKey in kpiScope)) {
                        const childKey = allKeys.find((childKey_) => childKey_ in kpiScope);

                        if (childKey && organisation) {
                           this.#changeKPIScope(childKey, kpiScope[childKey].id, kpiScope.from, kpiScope.to, organisation, kpiScope, false);
                        }
                     }
                  });
               })
            )
            .catch((error) => {
               this.setError(error);
            });
      }

      if (kpiScopes.some(({ id }) => id > 0)) {
         await this.httpClient
            .patch(
               `/${this.version}/${this.entityName}`,
               kpiScopes.filter(({ id }) => id > 0)
            )
            .then((resp) =>
               applyTransaction(() => {
                  returnValues.push(...resp.data);
                  resp.data.forEach((kpiScope: KpiScope) => {
                     if (allKeys.some((childKey) => childKey in kpiScope)) {
                        const childKey = allKeys.find((childKey_) => childKey_ in kpiScope);

                        const organisation = organisations.find(({ id }) => id === kpiScope.organisationId);

                        if (childKey && organisation) {
                           this.#changeKPIScope(childKey, kpiScope[childKey].id, kpiScope.from, kpiScope.to, organisation, kpiScope, false);
                        }
                     }
                  });
               })
            )
            .catch((error) => {
               this.setError(error);
            });
      }

      return returnValues;
   }

   getScopeQueryParams() {
      const dependencies: QueryDependency[] = [];

      dependencies.push({ observable: activeOrganisationId$.pipe(filterNilValue()), key: 'organisationId' });
      dependencies.push({ observable: periodContext$.pipe(filterNilValue()), key: 'periodContext' });
      dependencies.push({ observable: includeSubsidiaries$.pipe(filterNilValue()), key: 'includeSubsidiaries' });
      dependencies.push({ observable: activeReportingStandardId$.pipe(filterNilValue()), key: 'reportingStandardId' });

      return combineQueries(dependencies.map((d) => d.observable)).pipe(
         distinctUntilChanged(isEqual),
         map((values) => {
            const {
               organisationId,
               periodContext,
               includeSubsidiaries,
               reportingStandardId,
            }: {
               organisationId: Organisation['id'];
               periodContext: Period;
               includeSubsidiaries: boolean;
               reportingStandardId: ReportingStandard['id'];
            } = values.reduce((acc: QueryResolvedValues, value: any, index: number) => {
               acc[dependencies[index].key] = value;
               return acc;
            }, {});

            const urlParamsSubClass = new URLSearchParams();

            if (organisationId) {
               urlParamsSubClass.set('organisationId', String(organisationId));
            }

            if (periodContext) {
               urlParamsSubClass.set('from', periodContext.from);
               urlParamsSubClass.set('to', periodContext.to);
            }

            if (typeof includeSubsidiaries === 'boolean') {
               urlParamsSubClass.set('includeSubsidiaries', String(includeSubsidiaries));
            }

            if (reportingStandardId) {
               urlParamsSubClass.set('reportingStandardId', String(reportingStandardId));
            }

            if (periodContext && organisationId && reportingStandardId && typeof includeSubsidiaries === 'boolean') {
               return `?${urlParamsSubClass.toString()}`;
            }
         })
      );
   }

   async getScopes(force = false) {
      if (this.scopesSubscription === undefined || this.scopesSubscription?.closed || force) {
         this.scopesSubscription = this.scopesObservable?.subscribe((queryString) => {
            if (typeof queryString === 'string') {
               if (this.scopesController) {
                  this.scopesController.abort();
               }

               this.scopesController = new AbortController();

               this.kpiScopesStore?.setLoading(true);

               this.httpClient
                  .get(`/${this.version}/${this.entityName}${queryString}`, {
                     signal: this.scopesController.signal,
                  })
                  .then((resp) => this.kpiScopesStore?.set(resp.data, { loading: false } as any))
                  .catch((error) => {
                     if (!((error as AxiosError)?.name === 'AbortError' || (error as AxiosError)?.code === 'ERR_CANCELED')) {
                        this.setError(error as AxiosError, this.kpiScopesStore);
                     }
                     this.kpiScopesStore?.setLoading(false);
                  });
            }

            return undefined;
         });
      }
   }

   enqueueAttachmentUpload(kpiScopeId: KpiScope['id'], file: File) {
      this.kpiScopesStore?.update((prevState) => ({
         ...prevState,
         pendingUploads: [
            ...prevState.pendingUploads,
            { kpiScopeId, file, name: file.name, id: Math.min(0, ...prevState.pendingUploads.map(({ id }) => id)) - 1 },
         ],
      }));
   }

   unqueueAttachmentUpload(attachmentId: Attachment['id']) {
      this.kpiScopesStore?.update((prevState) => ({
         ...prevState,
         pendingUploads: prevState.pendingUploads.filter(({ id }) => id !== attachmentId),
      }));
   }

   enqueueAttachmentDelete(deletionInfo: any) {
      this.kpiScopesStore?.update((prevState) => ({
         ...prevState,
         pendingDeletions: [...prevState.pendingDeletions, deletionInfo],
      }));
   }

   async syncAttachments() {
      const storeValue = this.kpiScopesStore?.getValue();

      const { pendingDeletions, pendingUploads } = storeValue ?? { pendingDeletions: [], pendingUploads: [] };

      const promisesDelete: Promise<any>[] = [];
      const promisesUpload: Promise<any>[] = [];

      pendingDeletions
         .filter(({ kpiScopeId }) => kpiScopeId > 0)
         .forEach(({ attachmentId, kpiScopeId }) =>
            promisesDelete.push(this.httpClient.delete(`/${this.version}/kpiscopes/${kpiScopeId}/attachments/${attachmentId}`))
         );

      pendingUploads
         .filter(({ kpiScopeId }) => kpiScopeId > 0)
         .forEach(({ kpiScopeId, file }) => {
            const formData = new FormData();
            formData.append('attachment', file);
            promisesUpload.push(this.httpClient.post(`/${this.version}/kpiscopes/${kpiScopeId}/attachments`, formData));
         });

      await Promise.all(promisesDelete);

      const newAttachments = await Promise.all(promisesUpload);

      pendingDeletions.forEach(({ attachmentId, kpiScopeId }) =>
         this.kpiScopesStore?.update(kpiScopeId, ({ attachments }: { attachments: Attachment[] }) => ({
            attachments: arrayRemove(attachments, attachmentId),
         }))
      );

      newAttachments.forEach(({ data }, index) =>
         this.kpiScopesStore?.update(pendingUploads[index].kpiScopeId, ({ attachments }: { attachments: Attachment[] }) => ({
            attachments: arrayAdd(attachments, data),
         }))
      );

      this.kpiScopesStore?.update((prevState) => ({
         ...prevState,
         pendingUploads: [],
         pendingDeletions: [],
      }));
   }

   async recalculateScopes() {
      return this.httpClient.post(`/${this.version}/scoping/esrs/recalculate`).catch((error) => {
         this.setError(error, this.kpiScopesStore);
      });
   }

   async uploadFiles(entityId: KpiScope['id'], files: File[] = [], organisationId: Organisation['id'], from: string, to: string) {
      return uploadFilesCommon({
         httpClient: this.httpClient,
         version: this.version,
         entityName: this.entityName,
         entityId,
         files,
         organisationId,
         from,
         to,
         store: this.kpiScopesStore,
         setError: (error) => this.setError(error, this.kpiScopesStore),
      });
   }

   async deleteAttachment(kpiScopeId: KpiScope['id'], kpiScopeAttachmentId: Attachment['id']) {
      return deleteAttachmentHandler({
         httpClient: this.httpClient,
         version: this.version,
         entityName: this.entityName,
         store: this.kpiScopesStore,
         entityId: kpiScopeId,
         entityAttachmentId: kpiScopeAttachmentId,
         setError: (error) => this.setError(error, this.kpiScopesStore),
      });
   }

   hasActiveEntity(entityId?: KpiScope['id']) {
      return this.kpiScopesQuery?.hasActive(entityId) ?? false;
   }

   async updateEntities(updatedEntities: KpiScope[], updateStore = false) {
      const store = this.kpiScopesStore;

      return this.httpClient
         .patch(`/${this.version}/${this.entityName}`, updatedEntities)
         .then((resp) => {
            if (updateStore) {
               store?.upsertMany(resp.data);
               if (this.paginate && this.paginator) {
                  this.paginator.refreshCurrentPage();
                  this.pageChanges$?.next(this.getPageChangesCombinedObservable());
               }
            }

            return resp.data;
         })
         .catch((error) => {
            this.setError(error);
         });
   }

   async createEntity(
      entity: Partial<Omit<KpiScope, 'id' | 'kpiTopics'>> & {
         kpiTopicIds?: number[] | null;
      },
      fetchAssociations = false,
      additionalQueryParams = '',
      addToStore = true
   ) {
      if (entity.subSubTopicIds) {
         delete entity.subSubTopicIds;
      }

      if (entity.subTopicIds) {
         delete entity.subTopicIds;
      }

      if (entity.topicIds) {
         delete entity.topicIds;
      }

      return super.createEntity(entity, fetchAssociations, additionalQueryParams, addToStore, this.kpiScopesStore);
   }

   async updateEntity(entityId: KpiScope['id'], changes: any, addToStore = true) {
      return super.updateEntity(entityId, changes, addToStore, this.kpiScopesStore);
   }

   async createEntities(entities: Partial<KpiScope>[], fetchAssociations = false, additionalQueryParams = undefined, addToStore = false) {
      return super.createEntity(entities, fetchAssociations, additionalQueryParams, addToStore, this.kpiScopesStore);
   }

   async deleteEntities(entityIds: KpiScope['id'][], updateStore = false) {
      const store = this.kpiScopesStore;

      return this.httpClient
         .delete(`/${this.version}/${this.entityName}`, { data: entityIds })
         .then((resp) => {
            if (updateStore) {
               store?.remove(entityIds);
               if (this.paginate && this.paginator) {
                  this.paginator.refreshCurrentPage();
                  this.pageChanges$?.next(this.getPageChangesCombinedObservable());
               }
            }

            return resp.data;
         })
         .catch((error) => {
            this.setError(error);
         });
   }

   async setActiveEntity(entityUniqueIdentifier: KpiScope['id'] | null, loadCollections = true, refresh = false, loadCollectionParams = undefined) {
      return super.setActiveEntity(entityUniqueIdentifier, loadCollections, refresh, loadCollectionParams, {}, this.kpiScopesStore);
   }

   getLoading() {
      return this.kpiScopesQuery?.getValue().loading;
   }

   getError() {
      return this.kpiScopesQuery?.getValue().error;
   }
   async createOrUpdateScope({ id = -1, changes, onSuccess }: { id?: KpiScope['id']; changes: any; onSuccess: (_resp: KpiScope[]) => void }) {
      const isEdit = id > 0;

      // Decide between PATCH and POST
      const request = isEdit ? this.updateEntity(id, changes, false) : this.createEntity(changes, false, undefined, false);

      try {
         // Perform the request and invoke your callback with the response
         const resp = await request;
         if (resp != null) {
            onSuccess(resp);
         }
      } catch (error: any) {
         this.setError(error);
      }
   }

   cancelSuggestions() {
      if (this.suggestionsController) {
         this.suggestionsController.abort();
         this.suggestionsController = null;
      }
   }

   async suggestIROs(kpiTopicIds: (number | string)[]) {
      if (this.suggestionsController) {
         this.suggestionsController.abort();
      }

      this.suggestionsController = new AbortController();

      const { signal } = this.suggestionsController;

      return trackAiAction({ action: 'iroSuggestion', run: () => this.httpClient.post(`/${this.version}/ai/iros`, { kpiTopicIds }, { signal }) })
         .then((resp) => {
            return resp.data;
         })
         .catch((error) => {
            this.setError(error);
         });
   }

   async determineMateriality(
      kpiScope: Pick<
         KpiScope,
         | 'from'
         | 'to'
         | 'organisationId'
         | 'scale'
         | 'scope'
         | 'remediability'
         | 'materialityType'
         | 'valence'
         | 'likelihood'
         | 'sizeOfFinancialEffect'
         | 'isHumanRightsImpact'
         | 'type'
      > & { kpiTopics: { id: number }[] }
   ) {
      return this.httpClient
         .post(`/${this.version}/scoping/esrs/materiality`, kpiScope)
         .then((resp) => resp.data)
         .catch((error) => {
            this.setError(error);
         });
   }
}
